/**
 * Types for Zoom webhook events as stored in Firestore.
 * The webhook handler writes `event`, `payload`, and `timestamp` (server timestamp),
 * not Zoom's `event_ts`. Use these types when reading from the zoomWebhookEvents collection.
 */

import {z} from 'zod';
import {FirestoreTimestampSchema} from '../types';

// ============================================================================
// Breakout room event (join/leave) as stored
// ============================================================================

export const ZoomWebhookEventBreakoutRoomTypeSchema = z.enum([
  'meeting.participant_joined_breakout_room',
  'meeting.participant_left_breakout_room',
]);

/** Event strings for Firestore `where('event', 'in', …)` on breakout room logs. */
export const ZOOM_WEBHOOK_EVENT_BREAKOUT_ROOM_TYPES =
  ZoomWebhookEventBreakoutRoomTypeSchema.options;

export type ZoomWebhookEventBreakoutRoomType = z.infer<
  typeof ZoomWebhookEventBreakoutRoomTypeSchema
>;

const PayloadParticipantSchema = z
  .object({
    id: z.string(),
    user_id: z.string(),
    user_name: z.string(),
  })
  .loose();

const PayloadObjectSchema = z
  .object({
    participant: PayloadParticipantSchema,
    breakout_room_uuid: z.string(),
  })
  .loose();

const PayloadSchema = z
  .object({
    object: PayloadObjectSchema,
  })
  .loose();

/**
 * Schema for a single breakout room join or leave event as stored in Firestore
 * by the Zoom webhook. Uses `timestamp` (server timestamp when the doc was
 * written), not Zoom's `event_ts`.
 */
export const ZoomWebhookEventBreakoutRoomSchema = z
  .object({
    event: ZoomWebhookEventBreakoutRoomTypeSchema,
    /** When the event was stored (Firestore server timestamp). */
    timestamp: FirestoreTimestampSchema,
    payload: PayloadSchema,
  })
  .loose();

export type ZoomWebhookEventBreakoutRoom = z.infer<
  typeof ZoomWebhookEventBreakoutRoomSchema
>;
